import { useState } from 'react';
import { FaceSmileIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { QUICK_REACT_EMOJIS } from '@/config/emoji-config';
import { auth } from '@/config/firebase';

interface Props {
  /** emoji -> firebaseUids who reacted (prayer.encouragementReactions[noteId]). */
  reactions: Record<string, string[]> | undefined;
  /** Disable while the note only exists optimistically (temp- id). */
  disabled?: boolean;
  onToggle: (emoji: string, reacted: boolean) => void;
}

/**
 * Emoji reaction pills + quick-react bar for a single encouragement note.
 * Reaction membership is keyed by Firebase UID (not PG user id) — same
 * contract as chat message reactions and the Expo prayer detail screen.
 */
export default function EncouragementReactions({ reactions, disabled, onToggle }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const uid = auth.currentUser?.uid;

  const entries = Object.entries(reactions || {}).filter(([, uids]) => (uids?.length ?? 0) > 0);

  return (
    <div className="mt-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {entries.map(([emoji, uids]) => {
          const reacted = Boolean(uid && uids.includes(uid));
          return (
            <button
              key={emoji}
              disabled={disabled}
              onClick={() => onToggle(emoji, reacted)}
              className={clsx(
                'flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition disabled:opacity-50',
                reacted
                  ? 'border-flock-navy-400 bg-flock-navy-100 text-flock-navy-800'
                  : 'border-flock-gray-200 bg-white text-flock-navy-600 hover:bg-flock-navy-50'
              )}
              title={reacted ? 'Remove reaction' : 'React'}
            >
              <span>{emoji}</span>
              <span className="font-medium">{uids.length}</span>
            </button>
          );
        })}
        <button
          disabled={disabled}
          onClick={() => setPickerOpen((v) => !v)}
          className={clsx(
            'rounded-full p-1 text-flock-navy-300 transition hover:bg-flock-navy-50 hover:text-flock-navy-600 disabled:opacity-40',
            // Revealed on row hover (rows use `group`); always visible while open
            pickerOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
          )}
          title="React to this encouragement"
        >
          <FaceSmileIcon className="h-4 w-4" />
        </button>
      </div>

      {pickerOpen && (
        <div className="mt-1 flex flex-wrap items-center gap-0.5 rounded-full border border-flock-gray-200 bg-white px-2 py-1 shadow-sm">
          {QUICK_REACT_EMOJIS.map((emoji) => {
            const reacted = Boolean(uid && reactions?.[emoji]?.includes(uid));
            return (
              <button
                key={emoji}
                onClick={() => {
                  onToggle(emoji, reacted);
                  setPickerOpen(false);
                }}
                className="rounded-full px-1 py-0.5 text-base transition hover:scale-125"
              >
                {emoji}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
